import React, { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import Message from 'carbon-react/lib/components/message';
import { MessageVariant } from 'carbon-react/lib/components/message/message.component';

import * as styles from './toast-context.scss';

export type ToastId = string | number;

export interface ToastOptions {
  autoClose?: number | false;
  style?: React.CSSProperties;
}

interface ToastItem {
  id: ToastId;
  message: ReactNode;
  variant: MessageVariant;
  options: ToastOptions;
}

interface ToastContextValue {
  show: (message: ReactNode, variant: MessageVariant, options?: ToastOptions) => ToastId;
  dismiss: (id: ToastId) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_AUTO_CLOSE_MS = 5000;

export const useToastContext = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

// Module-level reference so imperative `toastSuccess`/`toastError` can work
// without needing React context access.
let globalToastRef: ToastContextValue | null = null;

export const getGlobalToast = (): ToastContextValue | null => globalToastRef;

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idCounter = useRef(0);
  const timers = useRef<Map<ToastId, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: ToastId) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (message: ReactNode, variant: MessageVariant, options: ToastOptions = {}): ToastId => {
      idCounter.current += 1;
      const id = idCounter.current;
      const item: ToastItem = { id, message, variant, options };

      setToasts(prev => [...prev, item]);

      const autoClose = options.autoClose !== undefined ? options.autoClose : DEFAULT_AUTO_CLOSE_MS;
      if (autoClose !== false) {
        const timer = setTimeout(() => {
          dismiss(id);
        }, autoClose);
        timers.current.set(id, timer);
      }

      return id;
    },
    [dismiss]
  );

  const contextValue: ToastContextValue = { show, dismiss };

  // Keep global ref in sync
  globalToastRef = contextValue;

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className={styles.toastContainer}>
        {toasts.map(t => (
          <Message key={t.id} open variant={t.variant} showCloseIcon onDismiss={() => dismiss(t.id)}>
            {t.message}
          </Message>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
